<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import KeywordComboBox from './KeywordComboBox.vue';
import CreateCatalogEntryDialog from './CreateCatalogEntryDialog.vue';
import ManageAliasesDialog from './ManageAliasesDialog.vue';
import { catalogItemLabel, catalogItemKeywords } from '../helpers';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value => ['make', 'model', 'trim', 'color'].includes(value),
  },
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  parentId: {
    type: [String, Number],
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  hasError: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useI18n();

const createDialogRef = ref(null);
const aliasesDialogRef = ref(null);

const options = computed(() =>
  props.items.map(item => ({
    value: item.id,
    label: catalogItemLabel(item, locale.value),
    keywords: catalogItemKeywords(item),
  }))
);

const selectedItem = computed(() =>
  props.items.find(item => item.id === props.modelValue)
);

const handleCreated = item => {
  emit('update:modelValue', item.id);
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="flex items-center justify-between">
      <span class="text-sm text-n-slate-11">{{ label }}</span>
      <div class="flex items-center">
        <Button
          v-if="selectedItem"
          icon="i-lucide-tags"
          variant="ghost"
          color="slate"
          size="xs"
          type="button"
          :title="t('WGPT_CAR_LISTINGS.CATALOG.MANAGE_ALIASES')"
          @click="aliasesDialogRef?.open()"
        />
        <Button
          icon="i-lucide-plus"
          variant="ghost"
          color="slate"
          size="xs"
          type="button"
          :disabled="disabled"
          :title="t(`WGPT_CAR_LISTINGS.CATALOG.ADD.${type}`)"
          @click="createDialogRef?.open()"
        />
      </div>
    </div>
    <KeywordComboBox
      :model-value="modelValue"
      :options="options"
      :disabled="disabled"
      :placeholder="placeholder"
      :message="message"
      :has-error="hasError"
      @update:model-value="emit('update:modelValue', $event)"
    />
    <CreateCatalogEntryDialog
      ref="createDialogRef"
      :type="type"
      :parent-id="parentId"
      @created="handleCreated"
    />
    <ManageAliasesDialog
      ref="aliasesDialogRef"
      :type="type"
      :item="selectedItem"
    />
  </div>
</template>
